import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  deleteDoc,
  query,
  where,
  writeBatch
} from "firebase/firestore";
import { db, LEGACY_TENANT_ID } from "@/lib/firebase";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

export interface Folder {
  id: string;
  name: string;
  userId: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  color?: string;
  sortOrder?: number;
}

export const FOLDERS_COLLECTION = "folders";

/**
 * Fetch all folders for a specific user
 */
export const fetchUserFolders = async (
  userId: string,
  tenantId: string = LEGACY_TENANT_ID
): Promise<Folder[]> => {
  try {
    logger.log("fetchUserFolders called with:", { userId, tenantId });

    const foldersRef = collection(db, FOLDERS_COLLECTION);
    const q = query(
      foldersRef,
      where("tenantId", "==", tenantId),
      where("userId", "==", userId)
    );

    const querySnapshot = await getDocs(q);
    logger.log("fetchUserFolders querySnapshot size:", querySnapshot.size);

    const folders = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Folder));

    logger.log("fetchUserFolders loaded folders:", folders);

    // Sort client-side by createdAt
    return folders.sort((a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  } catch (error) {
    logger.error("Error fetching user folders:", error);
    return [];
  }
};

/**
 * Create a new folder
 */
export const createFolder = async (
  name: string,
  userId: string,
  tenantId: string = LEGACY_TENANT_ID,
  color?: string
): Promise<Folder | null> => {
  try {
    logger.log("createFolder called with:", { name, userId, tenantId, color });

    const folderData = {
      name,
      userId,
      tenantId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      color: color || "#3B82F6", // Default blue
    };

    logger.log("Creating folder with data:", folderData);

    const docRef = await addDoc(collection(db, FOLDERS_COLLECTION), folderData);

    logger.log("Folder created successfully with ID:", docRef.id);

    toast({
      title: "Ordner erstellt",
      description: `Der Ordner "${name}" wurde erstellt.`
    });

    return { id: docRef.id, ...folderData };
  } catch (error) {
    logger.error("Error creating folder:", error);
    toast({
      title: "Fehler",
      description: "Der Ordner konnte nicht erstellt werden.",
      variant: "destructive"
    });
    return null;
  }
};

/**
 * Rename a folder
 */
export const renameFolder = async (
  folderId: string,
  newName: string
): Promise<boolean> => {
  try {
    const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
    await updateDoc(folderRef, {
      name: newName,
      updatedAt: new Date().toISOString()
    });

    toast({
      title: "Ordner umbenannt",
      description: `Der Ordner wurde zu "${newName}" umbenannt.`
    });
    return true;
  } catch (error) {
    logger.error("Error renaming folder:", error);
    toast({
      title: "Fehler",
      description: "Der Ordner konnte nicht umbenannt werden.",
      variant: "destructive"
    });
    return false;
  }
};

/**
 * Update folder color
 */
export const updateFolderColor = async (
  folderId: string,
  color: string
): Promise<boolean> => {
  try {
    const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
    await updateDoc(folderRef, {
      color,
      updatedAt: new Date().toISOString()
    });
    return true;
  } catch (error) {
    logger.error("Error updating folder color:", error);
    return false;
  }
};

/**
 * Delete a folder
 * Note: Leads in this folder will be moved to "no folder" (handled separately)
 */
export const deleteFolder = async (folderId: string): Promise<boolean> => {
  try {
    const folderRef = doc(db, FOLDERS_COLLECTION, folderId);
    await deleteDoc(folderRef);

    toast({
      title: "Ordner gelöscht",
      description: "Der Ordner wurde gelöscht. Die Leads wurden in 'Alle Leads' verschoben."
    });
    return true;
  } catch (error) {
    logger.error("Error deleting folder:", error);
    toast({
      title: "Fehler",
      description: "Der Ordner konnte nicht gelöscht werden.",
      variant: "destructive"
    });
    return false;
  }
};

/**
 * Remove folder assignment from all leads in a folder
 * Should be called before deleting a folder
 */
export const removeFolderFromLeads = async (
  folderId: string,
  tenantId: string = LEGACY_TENANT_ID
): Promise<boolean> => {
  try {
    const leadsRef = collection(db, "leads");
    const q = query(
      leadsRef,
      where("tenantId", "==", tenantId),
      where("folderId", "==", folderId)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return true;
    }

    const batch = writeBatch(db);

    querySnapshot.docs.forEach(docSnapshot => {
      batch.update(docSnapshot.ref, {
        folderId: null,
        lastUpdated: new Date().toISOString()
      });
    });

    await batch.commit();
    logger.log(`Removed folder assignment from ${querySnapshot.docs.length} leads`);
    return true;
  } catch (error) {
    logger.error("Error removing folder from leads:", error);
    return false;
  }
};

/**
 * Get folder by ID
 */
export const getFolderById = async (folderId: string): Promise<Folder | null> => {
  try {
    const foldersRef = collection(db, FOLDERS_COLLECTION);
    const q = query(foldersRef, where("__name__", "==", folderId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as Folder;
  } catch (error) {
    logger.error("Error getting folder by ID:", error);
    return null;
  }
};

/**
 * Count leads in a folder
 */
export const countLeadsInFolder = async (
  folderId: string,
  tenantId: string = LEGACY_TENANT_ID
): Promise<number> => {
  try {
    const leadsRef = collection(db, "leads");
    const q = query(
      leadsRef,
      where("tenantId", "==", tenantId),
      where("folderId", "==", folderId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.length;
  } catch (error) {
    logger.error("Error counting leads in folder:", error);
    return 0;
  }
};
